import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';

export type ChannelParticipantItem = {
  userId: string;
  name: string;
  email: string | null;
  isYou: boolean;
};

type DialogData = {
  channelName: string;
  participants: ChannelParticipantItem[];
};

@Component({
  selector: 'app-channel-participants-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatFormFieldModule, MatIconModule, MatInputModule],
  template: `
    <section class="member-dialog" aria-label="Group chat members">
      <header class="member-dialog__header" mat-dialog-title>
        <div class="member-dialog__headline">
          <h2>Members</h2>
          <p>{{ data.channelName | titlecase }} · {{ data.participants.length }}</p>
        </div>

        <button type="button" mat-icon-button class="member-dialog__close" aria-label="Close members dialog" (click)="close()">
          <mat-icon>close</mat-icon>
        </button>
      </header>

      <div mat-dialog-content class="member-dialog__content">
        <mat-form-field class="member-dialog__search" appearance="outline" subscriptSizing="dynamic">
          <mat-label>Search members</mat-label>
          <mat-icon matPrefix>search</mat-icon>
          <input
            matInput
            type="text"
            placeholder="Name or email"
            [value]="searchTerm()"
            (input)="onSearch(readValue($event))"
          />
        </mat-form-field>

        @if (filteredParticipants().length > 0) {
          <div class="member-dialog__list" role="list" aria-label="Chat members">
            @for (participant of filteredParticipants(); track participant.userId) {
              <div class="member-dialog__option" role="listitem">
                <span class="member-dialog__avatar">{{ getInitials(participant.name) }}</span>
                <span class="member-dialog__identity">
                  <strong>{{ participant.name | titlecase }} @if (participant.isYou) { <span class="member-dialog__you">You</span> }</strong>
                  @if (participant.email) { <small>{{ participant.email }}</small> }
                </span>
              </div>
            }
          </div>
        } @else {
          <div class="member-dialog__empty">
            <mat-icon>group</mat-icon>
            <h3>No matches</h3>
            <p>Try a different search.</p>
          </div>
        }
      </div>

      <footer class="member-dialog__actions" mat-dialog-actions align="end">
        <button type="button" mat-flat-button color="primary" (click)="close()">Done</button>
      </footer>
    </section>
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
    }

    :host,
    :host * {
      box-sizing: border-box;
    }

    .member-dialog {
      width: 100%;
      max-width: none;
      padding: 10px 10px 8px;
      background: var(--linear-surface);
      border-radius: 14px;
      overflow: hidden;
    }

    .member-dialog__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      padding: 0 8px;
    }

    .member-dialog__headline {
      min-width: 0;
      flex: 1 1 auto;
      text-align: left;
    }

    .member-dialog__headline h2,
    .member-dialog__headline p,
    .member-dialog__empty h3,
    .member-dialog__empty p {
      margin: 0;
    }

    .member-dialog__headline h2 {
      color: var(--linear-ink);
      font-size: 1.2rem;
      line-height: 1.15;
      text-align: left;
    }

    .member-dialog__headline p {
      margin-top: 6px;
      color: var(--linear-muted);
      font-size: 0.9rem;
    }

    .member-dialog__close {
      width: 36px;
      height: 36px;
      min-width: 36px;
      min-height: 36px;
      flex: 0 0 auto;
      color: var(--mat-sys-on-surface-variant);
    }

    .member-dialog__close:hover {
      background: var(--mat-sys-surface-container-highest);
    }

    .member-dialog__content {
      padding: 0 10px 2px;
      margin: 0;
    }

    .member-dialog__search {
      width: 100%;
      margin-top: 14px;
    }

    .member-dialog__search .mat-icon {
      color: var(--linear-muted);
      font-size: 18px;
      width: 18px;
      height: 18px;
      margin-right: 8px;
    }

    .member-dialog__list {
      display: grid;
      gap: 0.65rem;
      max-height: min(22rem, 50vh);
      overflow: auto;
      padding-right: 2px;
    }

    .member-dialog__option {
      display: flex;
      align-items: center;
      gap: 0.85rem;
      width: 100%;
      min-width: 0;
      border: 1px solid var(--linear-border);
      border-radius: 14px;
      padding: 0.8rem 0.9rem;
      background: var(--linear-surface);
      color: var(--linear-ink);
    }

    .member-dialog__avatar {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 2.5rem;
      height: 2.5rem;
      border-radius: 999px;
      flex: 0 0 auto;
      background: color-mix(in srgb, var(--mat-sys-primary) 18%, var(--linear-surface));
      color: var(--mat-sys-primary);
      font-weight: 700;
      letter-spacing: 0.05em;
    }

    .member-dialog__identity {
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .member-dialog__identity strong {
      font-size: 0.95rem;
      color: var(--linear-ink);
      font-weight: 700;
      display: inline-flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
    }

    .member-dialog__identity small {
      color: var(--linear-muted);
      font-size: 0.85rem;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      max-width: 26rem;
    }

    .member-dialog__you {
      display: inline-flex;
      align-items: center;
      padding: 2px 10px;
      border-radius: 999px;
      font-size: 0.75rem;
      font-weight: 700;
      letter-spacing: 0.04em;
      color: var(--mat-sys-primary);
      background: color-mix(in srgb, var(--mat-sys-primary) 14%, transparent);
    }

    .member-dialog__empty {
      display: grid;
      justify-items: center;
      text-align: center;
      gap: 6px;
      padding: 18px 12px 4px;
      color: var(--linear-muted);
    }

    .member-dialog__empty mat-icon {
      font-size: 32px;
      width: 32px;
      height: 32px;
      color: color-mix(in srgb, var(--mat-sys-primary) 58%, var(--linear-muted));
    }

    .member-dialog__empty h3 {
      color: var(--linear-ink);
      font-size: 1rem;
      margin-top: 4px;
    }

    .member-dialog__actions {
      padding: 10px 8px 0;
      margin: 0;
    }
  `],
})
export class ChannelParticipantsDialogComponent {
  protected readonly dialogRef = inject(MatDialogRef<ChannelParticipantsDialogComponent>);
  protected readonly data = inject<DialogData>(MAT_DIALOG_DATA);

  protected readonly searchTerm = signal('');

  protected readonly filteredParticipants = computed(() => {
    const query = this.searchTerm().trim().toLowerCase();
    if (!query) {
      return this.data.participants;
    }

    return this.data.participants.filter((participant) => {
      const haystack = `${participant.name} ${participant.email ?? ''}`.toLowerCase();
      return haystack.includes(query);
    });
  });

  protected close(): void {
    this.dialogRef.close();
  }

  protected readValue(event: Event): string {
    return (event.target as HTMLInputElement | null)?.value ?? '';
  }

  protected onSearch(value: string): void {
    this.searchTerm.set(value);
  }

  protected getInitials(name: string): string {
    return name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('');
  }
}

